// Resolves a react-router pathname to a clean, human-readable page name.
// Strips all route IDs (Mongo ObjectIds, numeric IDs) and any opaque token-like segment —
// never leaks an identifier or secret (FR-014). Takes location.pathname only; callers must
// never pass query strings.

const ID_SEGMENT = /^[0-9a-fA-F]{20,}$|^\d+$/;
// A route slug: short kebab/snake identifier of ASCII letters/digits with hyphens or underscores.
// Anything else (email-verification codes, Firebase oobCodes, opaque tokens, base64/hex blobs
// beyond MAX_SLUG_LENGTH) is treated as an opaque segment and stripped so it never reaches
// Mixpanel via page_name. `/reset-password/<token>` and `/verify-email/<code>` are the
// concrete high-risk cases this guards.
const SLUG_SEGMENT = /^[A-Za-z][A-Za-z0-9_-]*$/;
const MAX_SLUG_LENGTH = 32;

function isOpaqueSegment(segment: string): boolean {
  return !SLUG_SEGMENT.test(segment) || segment.length > MAX_SLUG_LENGTH;
}

function stripIdSegments(pathname: string): string[] {
  return pathname
    .split("/")
    .filter(Boolean)
    .filter((segment) => !ID_SEGMENT.test(segment) && !isOpaqueSegment(segment));
}

function humanize(segment: string): string {
  return segment.replace(/[-_]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

// Curated names for the pages central to the audited workflows, keyed by the
// ID-stripped segment path (see contracts/event-catalog.md for the source workflows).
const PAGE_NAMES: Record<string, string> = {
  "": "Home",
  "company/admin-view": "Company Dashboard",
  "companies-list": "Companies",
  "add-company": "Add Company",
  account: "Account Settings",
  "switch-company": "Switch Company",
  signin: "Sign In",
  "forgot-password": "Forgot Password",
  "reset-password": "Reset Password",
  "verify-email": "Verify Email",
  "invalid-token": "Invalid Token",
  "invitation-revoked": "Invitation Revoked",
  "investor/verify-email": "Investor Verify Email",
  "investor/invalid-token": "Investor Invalid Token",
  "nominee/verify-email": "Nominee Verify Email",
  "nominee/invalid-token": "Nominee Invalid Token",
  "create-account": "Create Account",
  "investor/create-account": "Investor Create Account",
  "nominee/create-account": "Nominee Create Account",
  "company/distributions": "Distributions",
  "company/distributions/create": "Create Distribution",
  "company/distributions/edit": "Edit Distribution",
  "company/distributions/report": "Distribution Report",
  "company/settings/distributions": "Distribution Settings",
  "company/settings/redemptions": "Redemption Settings",
  "investor/company/investor-view/redeem": "Redemption",
  "investor/company/investor-view/apply": "Apply for units",
  "company/settings/approvals": "Approvals Settings",
  "company/settings/administrators": "Administrators",
  "company/settings": "Company Settings",
  "company/approvals": "Approvals",
  "company/shares-register": "Shares Register",
  "company/shares-register/issue-share": "Issue Shares",
  "company/shares-register/transfer-shares": "Transfer Shares",
  "company/options-register": "Options Register",
  "company/notes-register": "Notes Register",
  "company/expiring-kyc-register": "Expiring KYC Register",
  "company/company-information": "Company Information",
  "company/company-information/notes-summary": "Notes Summary",
  "company/company-information/issue-notes": "Issue Note",
  "company/company-information/create-notes": "Create Note",
  "company/company-information/options/grant": "Grant Options",
  "company/company-information/options/pool-list": "Options Pools",
  "company/company-information/fundraise": "Fundraise Bank Account",
  "company/investor-relations/announcements": "Announcements",
  "company/investor-relations/announcement": "Announcement Detail",
  "company/investor-relations/contacts": "Investor Contacts",
  "company/investor-relations/invitations": "Investor Invitations",
  "company/investors/all": "Investors",
  "company/investors/add": "Add Investor",
  "company/investors/requests": "Investor Requests",
  "company/fundraises": "Fundraising Rounds",
  "company/create-fundraise": "Create Fundraise",
  "company/fundraise-offer": "Fundraise Offer",
  "company/documents": "Documents",
  "company/activity": "Activity",
  help: "Help",
  wallet: "Wallet",
  "investor/company/investor-view": "Investments",
  "investor/company/switch-holding": "Investor Profiles",
  "investor/company/investor-relations/announcement": "Announcements",
};

function fallbackName(segments: string[]): string {
  const meaningful = segments.filter((segment) => segment !== "company" && segment !== "investor");
  const base = meaningful.length ? meaningful : segments;
  return base.map(humanize).join(" ") || "Home";
}

export function resolvePageName(pathname: string): string {
  const segments = stripIdSegments(pathname);
  const key = segments.join("/");
  return PAGE_NAMES[key] ?? fallbackName(segments);
}
